import React, { createContext, useRef, useState } from 'react';

export interface GradeTrackLeaveContextValue {
  classMembersDirty: boolean;
  setClassMembersDirty: (v: boolean) => void;
  gradeTrackDirty: boolean;
  setGradeTrackDirty: (v: boolean) => void;
  academicCalendarDirty: boolean;
  setAcademicCalendarDirty: (v: boolean) => void;
  classScheduleDirty: boolean;
  setClassScheduleDirty: (v: boolean) => void;
  timetableSettingsDirty: boolean;
  setTimetableSettingsDirty: (v: boolean) => void;
  annualTimetableDirty: boolean;
  setAnnualTimetableDirty: (v: boolean) => void;
  adminLearningDirty: boolean;
  setAdminLearningDirty: (v: boolean) => void;
  workspaceLearningContentDirty: boolean;
  setWorkspaceLearningContentDirty: (v: boolean) => void;
  todoNoteDirty: boolean;
  setTodoNoteDirty: (v: boolean) => void;
  budgetNoteDirty: boolean;
  setBudgetNoteDirty: (v: boolean) => void;
  assignmentPointsNoteDirty: boolean;
  setAssignmentPointsNoteDirty: (v: boolean) => void;
  evaluationPlanDirty: boolean;
  setEvaluationPlanDirty: (v: boolean) => void;
  workspaceEvaluationPlanDirty: boolean;
  setWorkspaceEvaluationPlanDirty: (v: boolean) => void;
  pendingLeaveTo: string | null;
  setPendingLeaveTo: (path: string | null) => void;
  saveClassMembersRef: React.MutableRefObject<{
    save: () => Promise<void>;
    discard?: () => void;
  } | null>;
  saveGradeTrackRef: React.MutableRefObject<{
    save: () => Promise<void>;
    discard?: () => void;
  } | null>;
  saveAcademicCalendarRef: React.MutableRefObject<{
    save: () => Promise<void>;
    discard?: () => void;
  } | null>;
  saveClassScheduleRef: React.MutableRefObject<{
    save: () => Promise<void>;
    discard?: () => void;
  } | null>;
  saveTimetableSettingsRef: React.MutableRefObject<{
    save: () => Promise<void>;
    discard?: () => void;
  } | null>;
  saveAnnualTimetableRef: React.MutableRefObject<{
    save: () => Promise<void>;
    discard?: () => void;
  } | null>;
  saveAdminLearningRef: React.MutableRefObject<{
    save: () => Promise<void>;
    discard?: () => void;
  } | null>;
  saveWorkspaceLearningContentRef: React.MutableRefObject<{
    save: () => Promise<void>;
    discard?: () => void;
  } | null>;
  saveTodoNoteRef: React.MutableRefObject<{
    save: () => Promise<void>;
    discard?: () => void;
  } | null>;
  saveBudgetNoteRef: React.MutableRefObject<{
    save: () => Promise<void>;
    discard?: () => void;
  } | null>;
  saveAssignmentPointsNoteRef: React.MutableRefObject<{
    save: () => Promise<void>;
    discard?: () => void;
  } | null>;
  saveEvaluationPlanRef: React.MutableRefObject<{
    save: () => Promise<void>;
    discard?: () => void;
  } | null>;
  saveWorkspaceEvaluationPlanRef: React.MutableRefObject<{
    save: () => Promise<void>;
    discard?: () => void;
  } | null>;
  /** 프로그램 미저장(다른 스코프 워크스페이스 dirty 포함). 이탈 확인은 `/program/` 밖으로 나갈 때만 사용 */
  programProgressDirty: boolean;
  setProgramProgressDirty: (v: boolean) => void;
  saveProgramProgressRef: React.MutableRefObject<{
    save: () => Promise<void>;
    /** 저장 없이 이탈 시 로컬·워크스페이스 변경 폐기 */
    discard?: () => void;
  } | null>;
  programPendingLeaveTo: string | null;
  setProgramPendingLeaveTo: (path: string | null) => void;
  /** 편제 저장 등 이후 사이드바 편제 목록을 다시 불러올 때 증가 */
  sidebarTracksEpoch: number;
  bumpSidebarTracksEpoch: () => void;
}

const defaultValue: GradeTrackLeaveContextValue = {
  classMembersDirty: false,
  setClassMembersDirty: () => {},
  gradeTrackDirty: false,
  setGradeTrackDirty: () => {},
  academicCalendarDirty: false,
  setAcademicCalendarDirty: () => {},
  classScheduleDirty: false,
  setClassScheduleDirty: () => {},
  timetableSettingsDirty: false,
  setTimetableSettingsDirty: () => {},
  annualTimetableDirty: false,
  setAnnualTimetableDirty: () => {},
  adminLearningDirty: false,
  setAdminLearningDirty: () => {},
  workspaceLearningContentDirty: false,
  setWorkspaceLearningContentDirty: () => {},
  todoNoteDirty: false,
  setTodoNoteDirty: () => {},
  budgetNoteDirty: false,
  setBudgetNoteDirty: () => {},
  assignmentPointsNoteDirty: false,
  setAssignmentPointsNoteDirty: () => {},
  evaluationPlanDirty: false,
  setEvaluationPlanDirty: () => {},
  workspaceEvaluationPlanDirty: false,
  setWorkspaceEvaluationPlanDirty: () => {},
  setPendingLeaveTo: () => {},
  pendingLeaveTo: null,
  saveClassMembersRef: { current: null },
  saveGradeTrackRef: { current: null },
  saveAcademicCalendarRef: { current: null },
  saveClassScheduleRef: { current: null },
  saveTimetableSettingsRef: { current: null },
  saveAnnualTimetableRef: { current: null },
  saveAdminLearningRef: { current: null },
  saveWorkspaceLearningContentRef: { current: null },
  saveTodoNoteRef: { current: null },
  saveBudgetNoteRef: { current: null },
  saveAssignmentPointsNoteRef: { current: null },
  saveEvaluationPlanRef: { current: null },
  saveWorkspaceEvaluationPlanRef: { current: null },
  programProgressDirty: false,
  setProgramProgressDirty: () => {},
  saveProgramProgressRef: { current: null },
  setProgramPendingLeaveTo: () => {},
  programPendingLeaveTo: null,
  sidebarTracksEpoch: 0,
  bumpSidebarTracksEpoch: () => {},
};

export const GradeTrackLeaveContext = createContext<GradeTrackLeaveContextValue>(defaultValue);

export const GradeTrackLeaveProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [classMembersDirty, setClassMembersDirty] = useState(false);
  const [gradeTrackDirty, setGradeTrackDirty] = useState(false);
  const [academicCalendarDirty, setAcademicCalendarDirty] = useState(false);
  const [classScheduleDirty, setClassScheduleDirty] = useState(false);
  const [timetableSettingsDirty, setTimetableSettingsDirty] = useState(false);
  const [annualTimetableDirty, setAnnualTimetableDirty] = useState(false);
  const [adminLearningDirty, setAdminLearningDirty] = useState(false);
  const [workspaceLearningContentDirty, setWorkspaceLearningContentDirty] = useState(false);
  const [todoNoteDirty, setTodoNoteDirty] = useState(false);
  const [budgetNoteDirty, setBudgetNoteDirty] = useState(false);
  const [assignmentPointsNoteDirty, setAssignmentPointsNoteDirty] = useState(false);
  const [evaluationPlanDirty, setEvaluationPlanDirty] = useState(false);
  const [workspaceEvaluationPlanDirty, setWorkspaceEvaluationPlanDirty] = useState(false);
  const [pendingLeaveTo, setPendingLeaveTo] = useState<string | null>(null);
  const saveClassMembersRef = useRef<{ save: () => Promise<void>; discard?: () => void } | null>(null);
  const saveGradeTrackRef = useRef<{ save: () => Promise<void>; discard?: () => void } | null>(null);
  const saveAcademicCalendarRef = useRef<{ save: () => Promise<void>; discard?: () => void } | null>(null);
  const saveClassScheduleRef = useRef<{ save: () => Promise<void>; discard?: () => void } | null>(null);
  const saveTimetableSettingsRef = useRef<{ save: () => Promise<void>; discard?: () => void } | null>(null);
  const saveAnnualTimetableRef = useRef<{ save: () => Promise<void>; discard?: () => void } | null>(null);
  const saveAdminLearningRef = useRef<{ save: () => Promise<void>; discard?: () => void } | null>(null);
  const saveWorkspaceLearningContentRef = useRef<{ save: () => Promise<void>; discard?: () => void } | null>(null);
  const saveTodoNoteRef = useRef<{ save: () => Promise<void>; discard?: () => void } | null>(null);
  const saveBudgetNoteRef = useRef<{ save: () => Promise<void>; discard?: () => void } | null>(null);
  const saveAssignmentPointsNoteRef = useRef<{ save: () => Promise<void>; discard?: () => void } | null>(null);
  const saveEvaluationPlanRef = useRef<{ save: () => Promise<void>; discard?: () => void } | null>(null);
  const saveWorkspaceEvaluationPlanRef = useRef<{ save: () => Promise<void>; discard?: () => void } | null>(null);
  const [programProgressDirty, setProgramProgressDirty] = useState(false);
  const saveProgramProgressRef = useRef<{ save: () => Promise<void>; discard?: () => void } | null>(null);
  const [programPendingLeaveTo, setProgramPendingLeaveTo] = useState<string | null>(null);
  const [sidebarTracksEpoch, setSidebarTracksEpoch] = useState(0);
  const bumpSidebarTracksEpoch = () => setSidebarTracksEpoch((n) => n + 1);

  const value: GradeTrackLeaveContextValue = {
    classMembersDirty,
    setClassMembersDirty,
    gradeTrackDirty,
    setGradeTrackDirty,
    academicCalendarDirty,
    setAcademicCalendarDirty,
    classScheduleDirty,
    setClassScheduleDirty,
    timetableSettingsDirty,
    setTimetableSettingsDirty,
    annualTimetableDirty,
    setAnnualTimetableDirty,
    adminLearningDirty,
    setAdminLearningDirty,
    workspaceLearningContentDirty,
    setWorkspaceLearningContentDirty,
    todoNoteDirty,
    setTodoNoteDirty,
    budgetNoteDirty,
    setBudgetNoteDirty,
    assignmentPointsNoteDirty,
    setAssignmentPointsNoteDirty,
    evaluationPlanDirty,
    setEvaluationPlanDirty,
    workspaceEvaluationPlanDirty,
    setWorkspaceEvaluationPlanDirty,
    pendingLeaveTo,
    setPendingLeaveTo,
    saveClassMembersRef,
    saveGradeTrackRef,
    saveAcademicCalendarRef,
    saveClassScheduleRef,
    saveTimetableSettingsRef,
    saveAnnualTimetableRef,
    saveAdminLearningRef,
    saveWorkspaceLearningContentRef,
    saveTodoNoteRef,
    saveBudgetNoteRef,
    saveAssignmentPointsNoteRef,
    saveEvaluationPlanRef,
    saveWorkspaceEvaluationPlanRef,
    programProgressDirty,
    setProgramProgressDirty,
    saveProgramProgressRef,
    programPendingLeaveTo,
    setProgramPendingLeaveTo,
    sidebarTracksEpoch,
    bumpSidebarTracksEpoch,
  };

  return (
    <GradeTrackLeaveContext.Provider value={value}>
      {children}
    </GradeTrackLeaveContext.Provider>
  );
};
